import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FunctionsService } from 'src/app/services/api/functions.service';
import { formatScheduledPaymentDateDisplay } from 'src/entities/order/lib/apply-order-to-services';
import { OrderConfirmDialogComponent } from './order-confirm-dialog.component';
import { OrderFreezeDeadlineDialogComponent } from './order-freeze-deadline-dialog.component';
import { OrderRecalcHistoryDialogComponent } from './order-recalc-history-dialog.component';
import {
  applyConfirmedPreliminaryOrder,
  applyDeleteOrderDialogResult,
  applyFreezeDeadlineToServices
} from 'src/entities/order';
import {
  canDeleteServiceOrder as canDeleteServiceOrderFn,
  canShowCreditNote as canShowCreditNoteFn,
  canShowOrderPdf as canShowOrderPdfFn
} from 'src/entities/order/model/order-status.model';
import {
  applyDeletePurchaseNoteDialogResult,
  canDeletePurchaseNote as canDeletePurchaseNoteFn,
  DeletePurchaseNoteDialogResult,
  getServicePurchaseNoteId
} from 'src/entities/purchase-note';
import { buildDeletePurchaseNoteDialogConfig } from 'src/features/purchase-note/delete-purchase-note/ui/dialog-delete-np/delete-purchase-note-dialog.config';
import { DialogDeleteNpComponent } from 'src/features/purchase-note/delete-purchase-note/ui/dialog-delete-np/dialog-delete-np.component';
import { NotaCreditoProveedorComponent } from 'src/features/service-order/services-os-actions/ui/nota-credito-proveedor/nota-credito-proveedor.component';

@Component({
  selector: 'app-services-os-actions',
  templateUrl: './services-os-actions.component.html',
  styleUrls: ['./services-os-actions.component.css']
})
export class ServicesOsActionsComponent implements OnInit {
  @Input() row: any;
  @Input() model: any[] = [];
  /** Alimentos: bloque NP (PDF + eliminar). */
  @Input() showPurchaseNotePdf = false;
  @Output() pdfStatusChanged = new EventEmitter<void>();
  @Output() modelChanged = new EventEmitter<void>();

  constructor(
    public dialog: MatDialog,
    private readonly functions: FunctionsService
  ) {}

  ngOnInit(): void {}

  showOsGroup(row: any): boolean {
    return canShowOrderPdfFn(row) || canDeleteServiceOrderFn(row);
  }

  showNpGroup(row: any): boolean {
    return (
      Boolean(getServicePurchaseNoteId(row)) || canDeletePurchaseNoteFn(row)
    );
  }

  openDialogNotaCreditoProveedorComponent(os: any): void {
    this.dialog.open(NotaCreditoProveedorComponent, {
      width: '800px',
      minHeight: '490px',
      data: {
        fileNumber: os.fileNumber,
        nameProveedor: os.providerSnapshot?.nombre,
        idProveedor: os.providerSnapshot?.id,
        numberOS: os.order?.orderNumber,
        idItinerary: os.idItinerary,
        moneda: os.objTarifa?.monedaTarifa === 'DOLAR' ? 'Dólares' : 'Soles'
      }
    });
  }

  canDeleteServiceOrder(row: any): boolean {
    return canDeleteServiceOrderFn(row);
  }

  isPreliminary(row: any): boolean {
    return row?.order?.orderStatus === 'PRELIMINAR';
  }

  corteLabel(row: any): string {
    const label = formatScheduledPaymentDateDisplay(row?.order?.freezeDeadlineAt);
    return label === '-' ? 'Sin fecha' : label;
  }

  openConfirm(row: any): void {
    const orderId = row?.order?.orderId;
    if (!orderId) {
      return;
    }
    this.raiseOverlay();
    this.dialog
      .open(OrderConfirmDialogComponent, {
        width: '420px',
        maxWidth: '94vw',
        autoFocus: false,
        panelClass: 'corte-dialog-panel',
        data: {
          orderId,
          orderLabel: row?.order?.displayCode || row?.order?.orderNumber
        }
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result || !row?.order) {
          return;
        }
        // La OS puede tener varios servicios: se confirman todos en pantalla.
        applyConfirmedPreliminaryOrder(this.model, result);
        if (row.order) {
          row.order.orderStatus = result.orderStatus;
          row.order.displayCode = result.displayCode || row.order.displayCode;
          row.order.pdfStatus = result.pdfStatus;
          row.orderIssuanceFrozen = true;
        }
        this.modelChanged.emit();
      });
  }

  openCorte(row: any): void {
    const orderId = row?.order?.orderId;
    if (!orderId) {
      return;
    }
    this.raiseOverlay();
    this.dialog
      .open(OrderFreezeDeadlineDialogComponent, {
        width: '420px',
        maxWidth: '94vw',
        autoFocus: false,
        panelClass: 'corte-dialog-panel',
        data: {
          orderId,
          freezeDeadlineAt: row?.order?.freezeDeadlineAt,
          orderLabel: row?.order?.displayCode || row?.order?.orderNumber
        }
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result?.freezeDeadlineAt || !row?.order) {
          return;
        }
        const freezeDeadlineAt = `${result.freezeDeadlineAt}T00:00:00.000Z`;
        // La fecha es de la OS completa: se actualiza en todos sus servicios.
        applyFreezeDeadlineToServices(
          this.model,
          {
            orderId: row.order.orderId,
            orderNumber: row.order.orderNumber
          },
          freezeDeadlineAt
        );
        row.order.freezeDeadlineAt = freezeDeadlineAt;
        row.freezeDeadlineAt = freezeDeadlineAt;
        this.modelChanged.emit();
      });
  }

  openHistory(row: any): void {
    const orderId = row?.order?.orderId;
    if (!orderId) {
      return;
    }
    this.raiseOverlay();
    this.dialog.open(OrderRecalcHistoryDialogComponent, {
      width: '480px',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'os-history-panel',
      data: {
        orderId,
        lastRecalculatedAt: row?.order?.lastRecalculatedAt ?? null,
        autoAdjusted: row?.order?.autoAdjusted === true
      }
    });
  }

  private raiseOverlay(): void {
    const container = document.querySelector(
      '.cdk-overlay-container'
    ) as HTMLElement | null;
    if (container) {
      container.style.zIndex = '120000';
    }
  }

  canDeletePurchaseNote(row: any): boolean {
    return canDeletePurchaseNoteFn(row);
  }

  canShowCreditNote(row: any): boolean {
    return canShowCreditNoteFn(row);
  }

  openDialogPdfDelete(service: any): void {
    this.functions
      .openDeleteOrderDialog(service, this.model)
      .afterClosed()
      .subscribe((result) => {
        applyDeleteOrderDialogResult(this.model, result, service);
        if (!result?.success && result?.shouldRefresh) {
          this.functions.disparadorCambios.emit('reload-after-create-order');
        }
        if (result) {
          this.modelChanged.emit();
        }
      });
  }

  openDialogDeletePurchaseNote(service: any): void {
    this.dialog
      .open(
        DialogDeleteNpComponent,
        buildDeletePurchaseNoteDialogConfig({
          service,
          dataAll: this.model,
          contextLabel: 'Alimentos y bebidas'
        })
      )
      .afterClosed()
      .subscribe((result: DeletePurchaseNoteDialogResult | undefined) => {
        if (result?.shouldRefresh) {
          this.functions.disparadorCambios.emit('reload-after-create-order');
          this.modelChanged.emit();
          return;
        }

        applyDeletePurchaseNoteDialogResult(this.model, result, service);
        if (result?.success) {
          this.modelChanged.emit();
        }
      });
  }
}
